import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { FormControlLabel, Stack, Switch, TextField } from '@mui/material';
import { Business, Delete } from '@mui/icons-material';
import {
  useCreateOrganization,
  useDeleteOrganization,
  useOrganization,
  useOrganizations,
  useUpdateOrganization,
} from '@/hooks/useApi';
import { useAdminMode } from '@/hooks/useManage';
import { CatalogList, ErrorState, LoadingState, useCatalogParams } from '@/components';
import { FormPage, KebabMenu } from '@/components/manage';
import { useNotification } from '@/contexts/NotificationContext';
import { getApiErrorMessage } from '@/utils/apiError';
import type { Organization } from '@/types';

export const OrganizationsPage: React.FC = () => {
  const { q, page } = useCatalogParams();
  const { adminMode, create } = useAdminMode();
  const { data, isLoading, error, refetch } = useOrganizations({ page, limit: 20, q });

  return (
    <CatalogList
      title="Организации"
      icon={<Business color="primary" />}
      isLoading={isLoading}
      error={error}
      onRetry={refetch}
      totalPages={data?.totalPages}
      createHref={create.organization ? '/organizations/new' : undefined}
      emptyText="Организаций пока нет"
      rows={(data?.data ?? []).map((org) => ({
        id: org.id,
        title: org.name,
        subtitle: org.country ?? undefined,
        muted: !org.active,
        href: adminMode ? `/organizations/${org.id}/edit` : `/tournaments?organizationId=${org.id}`,
      }))}
    />
  );
};

const OrganizationFormInner: React.FC<{ org?: Organization }> = ({ org }) => {
  const navigate = useNavigate();
  const { showError, showSuccess } = useNotification();
  const { can } = useAdminMode();
  const [form, setForm] = useState({ name: org?.name ?? '', country: org?.country ?? '', active: org?.active ?? true });
  const [dirty, setDirty] = useState(false);
  const createOrg = useCreateOrganization();
  const updateOrg = useUpdateOrganization();
  const deleteOrg = useDeleteOrganization();

  const patch = (next: Partial<typeof form>) => {
    setForm((prev) => ({ ...prev, ...next }));
    setDirty(true);
  };

  const submit = async () => {
    try {
      if (org) await updateOrg.mutateAsync({ id: org.id, data: form });
      else await createOrg.mutateAsync(form);
      setDirty(false);
      showSuccess('Организация сохранена');
      navigate('/organizations', { replace: true });
    } catch (err) {
      showError(getApiErrorMessage(err, 'Не удалось сохранить организацию.'));
    }
  };

  return (
    <FormPage
      title={org ? 'Организация' : 'Новая организация'}
      onSubmit={() => void submit()}
      saving={createOrg.isPending || updateOrg.isPending}
      submitDisabled={!form.name || (org ? !can(org.permissions?.update) : false)}
      dirty={dirty}
      closeTo="/organizations"
      headerActions={
        org && (
          <KebabMenu
            items={[
              {
                id: 'delete',
                label: 'Удалить',
                icon: <Delete fontSize="small" />,
                danger: true,
                hidden: !can(org.permissions?.delete),
                confirm: { title: 'Удалить организацию?' },
                onClick: async () => {
                  await deleteOrg.mutateAsync(org.id);
                  navigate('/organizations', { replace: true });
                },
              },
            ]}
          />
        )
      }
    >
      <Stack spacing={2} sx={{ pt: 1 }}>
        <TextField label="Название" required fullWidth value={form.name} onChange={(e) => patch({ name: e.target.value })} />
        <TextField label="Страна" fullWidth value={form.country} onChange={(e) => patch({ country: e.target.value })} autoComplete="country-name" />
        <FormControlLabel
          sx={{ minHeight: 48 }}
          control={<Switch checked={form.active} onChange={(e) => patch({ active: e.target.checked })} />}
          label="Активна"
        />
      </Stack>
    </FormPage>
  );
};

export const OrganizationFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { data: org, isLoading, error, refetch } = useOrganization(id ?? '', { queryKey: ['organizations', id ?? ''], enabled: Boolean(id) });
  if (!id) return <OrganizationFormInner />;
  if (isLoading) return <LoadingState fullscreen />;
  if (error || !org) return <ErrorState message="Организация не найдена" onRetry={refetch} />;
  return <OrganizationFormInner key={org.id} org={org} />;
};
